import React from 'react';
import { useTranslation } from 'react-i18next';
import './AvailabilityBadge.css';

/**
 * AvailabilityBadge - Shows availability status on ad cards and detail pages
 * 
 * Props:
 * - status: 'available' | 'reserved' | 'sold' | 'expired' | 'unavailable' | 'hidden'
 * - reserved_until: ISO date string (for reserved status)
 * - last_refreshed_at: ISO date string (for stale hint)
 * - category: string (to determine stale threshold)
 * - size: 'sm' | 'md' | 'lg' (default: 'sm')
 * - showStaleHint: boolean (default: true)
 */
export function AvailabilityBadge({ 
  status = 'available', 
  reserved_until,
  last_refreshed_at,
  category,
  size = 'sm',
  showStaleHint = true
}) {
  const { t } = useTranslation();
  
  // Calculate days since last refresh
  const getDaysSinceRefresh = () => {
    if (!last_refreshed_at) return 0;
    try {
      const refreshDate = new Date(last_refreshed_at);
      const now = new Date();
      const diffTime = now - refreshDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return 0;
    }
  };
  
  // Get stale threshold based on category
  const getStaleThreshold = () => {
    const cat = (category || '').toLowerCase();
    if (cat.includes('vehicle') || cat.includes('car')) return 7;
    return 14;
  };
  
  const daysSinceRefresh = getDaysSinceRefresh();
  const staleThreshold = getStaleThreshold();
  const isStale = daysSinceRefresh > staleThreshold && status === 'available';
  
  // Format reserved_until date
  const formatReservedUntil = () => {
    if (!reserved_until) return '';
    try {
      const date = new Date(reserved_until);
      return date.toLocaleDateString();
    } catch {
      return '';
    }
  };
  
  // Get badge config
  const getBadgeConfig = () => {
    switch (status) {
      case 'available':
        return {
          label: t('availability.available'),
          className: 'badge-available',
          icon: null
        };
      case 'reserved':
        return {
          label: t('availability.reserved'),
          className: 'badge-reserved',
          icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          )
        };
      case 'sold':
        return {
          label: t('availability.sold'),
          className: 'badge-sold',
          icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          )
        };
      case 'expired':
        return {
          label: t('availability.expired'),
          className: 'badge-expired',
          icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          )
        };
      case 'unavailable':
        return {
          label: t('availability.unavailable'),
          className: 'badge-unavailable',
          icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
            </svg>
          )
        };
      case 'hidden':
        return {
          label: t('availability.hidden'),
          className: 'badge-hidden',
          icon: (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          )
        };
      default:
        return null;
    }
  };
  
  const config = getBadgeConfig();
  if (!config) return null;
  
  // Don't show "Available" badge on cards (it's the default state)
  // Only show it on detail pages or when there's a stale hint
  if (status === 'available' && !isStale && size === 'sm') {
    return null;
  }
  
  return (
    <div className={`availability-badge-wrapper size-${size}`} data-testid={`availability-badge-${status}`}>
      <span className={`availability-badge ${config.className}`}>
        {config.icon && <span className="badge-icon">{config.icon}</span>}
        <span className="badge-label">{config.label}</span>
      </span>
      
      {/* Reserved until info */}
      {status === 'reserved' && reserved_until && (
        <span className="reserved-until">
          {t('availability.reserved_until', { date: formatReservedUntil() })}
        </span>
      )}
      
      {/* Stale hint */}
      {showStaleHint && isStale && status === 'available' && (
        <div className="stale-hint">
          <span className="stale-warning">{t('availability.stale_hint')}</span>
          <span className="last-updated">
            {t('availability.updated_ago', { n: daysSinceRefresh })}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Get availability status from ad object
 * Handles various field names and formats
 */
export function getAvailabilityStatus(ad) {
  if (!ad) return 'available';
  
  // Check availability_status field (primary)
  const availability = ad.availability_status || ad.availability;
  if (availability) {
    const normalized = availability.toLowerCase();
    if (normalized === 'sold' || normalized === 'مباع') return 'sold';
    if (normalized === 'reserved' || normalized === 'محجوز') return 'reserved';
    if (normalized === 'expired' || normalized === 'منتهي') return 'expired';
    return 'available';
  }
  
  // Fallback: check sold_at field
  if (ad.sold_at) return 'sold';
  
  // Fallback: check is_sold flag
  if (ad.is_sold) return 'sold';
  
  return 'available';
}

// iter269: canonical "boost-eligible" status set.
// The user-reported bug "Boost says: Ad must be published first / No
// published ads to boost" came from this helper checking `status === 'published'`,
// while real published ads in this codebase actually carry `status === 'active'`.
//
// The canonical published / live / visible statuses are:
//   • 'active'      — primary status emitted by /api/ads/* read paths.
//   • 'live'        — synonym some older code paths emitted.
//   • 'published'   — legacy synonym; kept for back-compat.
//   • 'approved'    — moderation alias used by some seed data.
//   • 'shop_visible'— derived flag some older serializers exposed.
// Anything else (draft, pending_review, hidden_*, sold, rejected,
// removed_by_moderation, deleted) is NOT boost-eligible.
export const BOOST_ELIGIBLE_STATUSES = new Set([
  'active', 'live', 'published', 'approved', 'shop_visible',
]);

export function isBoostEligibleStatus(status) {
  if (!status) return false;
  return BOOST_ELIGIBLE_STATUSES.has(String(status).toLowerCase().trim());
}

/**
 * Check if ad is eligible for boosting
 * Rules:
 * - Must be in BOOST_ELIGIBLE_STATUSES (active / live / published / approved / shop_visible)
 * - Must be available (not sold/reserved/expired)
 * - Must not be hidden by user / reports
 * - Must not be deleted
 */
export function canBoostAd(ad) {
  if (!ad) return false;

  // iter269: canonical published-status check.
  if (!isBoostEligibleStatus(ad.status)) return false;

  // Must be available (not sold/reserved/expired).
  const availability = getAvailabilityStatus(ad);
  if (availability !== 'available') return false;

  // Must not be hidden / removed.
  if (ad.is_hidden) return false;
  if (ad.hidden_by_reports) return false;
  if (ad.removed_by_moderation) return false;

  return true;
}

/**
 * Check if ad is currently boosted
 */
export function isBoostActive(ad) {
  if (!ad?.is_boosted) return false;
  if (!ad?.boost_until) return false;
  
  const boostUntil = typeof ad.boost_until === 'string' 
    ? new Date(ad.boost_until) 
    : ad.boost_until;
    
  return boostUntil.getTime() > Date.now();
}

/**
 * Get boost remaining time in days/hours
 */
export function getBoostTimeRemaining(ad) {
  if (!isBoostActive(ad)) return null;
  
  const boostUntil = typeof ad.boost_until === 'string' 
    ? new Date(ad.boost_until) 
    : ad.boost_until;
  
  const remaining = boostUntil.getTime() - Date.now();
  const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  return { days, hours, total_ms: remaining };
}

export default AvailabilityBadge;
